import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Divider, Form, Input, message, notification, Popconfirm, Row, Select, Space, Spin, Table, Tabs, Typography } from "antd";
import { DeleteOutlined, InboxOutlined, SearchOutlined } from "@ant-design/icons";
import { BiDownload, BiFile, BiTrash } from "react-icons/bi";
import { FaCheckCircle, FaRegFileExcel, FaStar, FaTimesCircle } from "react-icons/fa";
import Highlighter from "react-highlight-words";
import Dragger from "antd/lib/upload/Dragger";
import { useSelector } from "react-redux";
import moment from "moment";

import { IContaSaldoDeContas, IFonteSaldoDeContas, IFonteVinculadaSaldoDeContas, IPlanilhaSaldoDeContas, ISolicitacaoAtivacao } from "../../../interfaces";
import { IOrgao, IOrgaoVinculado } from "../../../../Orgaos/interfaces";
import { ICreateConta, ICreateFonte, IDeleteConta, IDeleteFonte, IGetContasEPlanilha, IGetFontes, IGetFontesOrfas, IGetOrdenacaoFontes, IGetOrgaosVinculados, IOrdenacaoCategoriaItem, IOrdenacaoFonteItem, IUpdateCategoriaFonte, IUpdateConta, IUpdateContasFontes, IUpdateDescricaoFonte, IUpdateFonteFavorita, IUpdateOrdenacaoFontes } from "./interfaces";
import { IContasEPlanilhaResponse } from "../../../../interfaces";
import { stateGlobal } from "../../../../../../interfaces/Global";

import { EmitirErro } from "../../../../../../services/EmitirErro";
import SolicitacoesDeAtivacaoService from "../../../../../../services/http/services/SaldoDeContas/SolicitacoesDeAtivacaoService";
import { mask } from "../../../../../../utils/MascaraDeCampos";
import InputObservacaoEtapa from "../../components/InputObservacaoEtapa";
import { removeAcento } from "../../../../../../utils/RemoveAcentos";
import { useFontesColumnSearchProps } from "./hooks/useFontesColumnSearchProps";
import OrdenacaoFonteItem from "./components/OrdenacaoFonteItem";
import OrdenacaoList from "./components/OrdenacaoList";
import { onlyNumbers } from "../../../../utils";
import ContaForm from "./components/ContaForm";
import EditarFontesModal from "./components/EditarFontesModal";
import EtapaInfo from "../../components/EtapaInfo";
import BotaoLimparFiltros, { checkHasActiveFilters } from "../../../../components/BotaoLimparFiltros";
import BotaoLimparSelecao from "../../../../components/BotaoLimparSelecao";
import FontesTagsCell from "./components/FontesTagsCell";
import TabTitleCount from "../../components/TabTitleCount";
import InvestimentosTab from "./components/InvestimentosTab";

import './style.css';

function onlyNumbersAndLetters(value: string) {
    if (!value) {
        return "";
    }

    return value.replace(/[^a-zA-Z0-9]/g, '');
}

interface IProps {
    solicitacao: ISolicitacaoAtivacao | null;
    onUpdateOrgao: (orgao: IOrgao) => void;
    onEtapaIniciada: (solicitacao: ISolicitacaoAtivacao) => void;
    onEtapaConcluida: (solicitacao: ISolicitacaoAtivacao) => void;
    onUpdateObservacao: (solicitacao: ISolicitacaoAtivacao) => void;
    onGetOrdenacaoFontes: (data: IGetOrdenacaoFontes) => Promise<IOrdenacaoCategoriaItem[]>;
    onGetFontesOrfas?: (data: IGetFontesOrfas) => Promise<IOrdenacaoCategoriaItem[]>;
    onGetContasEPlanilha: (data: IGetContasEPlanilha) => Promise<IContasEPlanilhaResponse>;
    onUpdateFonteFavorita: (data: IUpdateFonteFavorita) => Promise<void>;
    onUpdateOrdenacaoFontes: (data: IUpdateOrdenacaoFontes) => Promise<IOrdenacaoCategoriaItem | null>;
    onUpdateCategoriaFonte: (data: IUpdateCategoriaFonte) => Promise<void>;
    onUpdateDescricaoFonte: (data: IUpdateDescricaoFonte) => Promise<IFonteSaldoDeContas | null>;
    onCreateFonte: (data: ICreateFonte) => Promise<IFonteSaldoDeContas | null>;
    onUpdateConta: (data: IUpdateConta) => Promise<IContaSaldoDeContas | null>;
    onCreateConta: (data: ICreateConta) => Promise<IContaSaldoDeContas | null>;
    onDeleteConta: (data: IDeleteConta) => Promise<void>;
    onEncerrarConta?: (contaId: number) => Promise<void>;
    onDeleteFonte?: (data: IDeleteFonte) => Promise<void>;
    onUpdateContasFontes?: (data: IUpdateContasFontes) => Promise<IContaSaldoDeContas[] | void>;
    onGetFontes: (data: IGetFontes) => Promise<IFonteSaldoDeContas[] | null>;
    onGetOrgaosVinculados: (data: IGetOrgaosVinculados) => Promise<IOrgaoVinculado[] | null>;
    recarregarContas?: boolean;
    onContasRecarregadas?: () => void;
    etapaAtiva?: boolean;
    isManutencao?: boolean;
}

export const AlinhamentoDeContasEFontes = ({
    solicitacao,
    onUpdateOrgao,
    onEtapaIniciada,
    onEtapaConcluida,
    onUpdateObservacao,
    onGetOrdenacaoFontes,
    onGetFontesOrfas,
    onGetContasEPlanilha,
    onUpdateFonteFavorita,
    onUpdateOrdenacaoFontes,
    onUpdateCategoriaFonte,
    onUpdateDescricaoFonte,
    onCreateFonte,
    onUpdateConta,
    onCreateConta,
    onDeleteConta,
    onEncerrarConta,
    onDeleteFonte,
    onUpdateContasFontes,
    onGetFontes,
    onGetOrgaosVinculados,
    recarregarContas,
    onContasRecarregadas,
    etapaAtiva = true,
    isManutencao,
}: IProps) => {
    const [planilha, setPlanilha] = useState<IPlanilhaSaldoDeContas | null>(null);
    const [contas, setContas] = useState<IContaSaldoDeContas[] | null>(null);
    const [activeTabKey, setActiveTabKey] = useState<string>('planilha');
    const [isLoadingPlanilha, setIsLoadingPlanilha] = useState<boolean>(true);
    const [isSubmitingPlanilha, setIsSubmitingPlanilha] = useState<boolean>(false);
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [isContaModalVisible, setIsContaModalVisible] = useState(false);
    const [isEditarFontesModalVisible, setIsEditarFontesModalVisible] = useState(false);
    const [selectedConta, setSelectedConta] = useState<IContaSaldoDeContas | null>(null);
    const [isDeletingConta, setIsDeletingConta] = useState(false);
    const [isEncerrandoConta, setIsEncerrandoConta] = useState(false);
    const [ordenacaoFontesCategorias, setOrdenacaoFontesCategorias] = useState<IOrdenacaoCategoriaItem[] | null>(null);
    const [searchTextFontes, setSearchTextFontes] = useState('');
    const [selectedCategoriaId, setSelectedCategoriaId] = useState<number | null>(null);
    const [isDeletingPlanilha, setIsDeletingPlanilha] = useState(false);
    const [isLoadingOrdenacaoFontes, setIsLoadingOrdenacaoFontes] = useState(false);

    const [ordenacaoFontesOrfasCategorias, setOrdenacaoFontesOrfasCategorias] = useState<IOrdenacaoCategoriaItem[] | null>(null);
    const [searchTextFontesOrfas, setSearchTextFontesOrfas] = useState('');
    const [selectedCategoriaOrfasId, setSelectedCategoriaOrfasId] = useState<number | null>(null);
    const [isLoadingFontesOrfas, setIsLoadingFontesOrfas] = useState(false);

    const [fileList, setFileList] = useState<any>([]);
    const planilhaFormRef = useRef<any>(null);
    const propsState = useSelector((state: stateGlobal) => state.global);
    const refContagemDoFiltro = useRef<Array<any>>([]);

    const etapa = solicitacao?.etapasSolicitacaoAtivacao?.find((e) => e.etapa.ordem === 2);
    const etapa3ConcluidaEm = solicitacao?.etapasSolicitacaoAtivacao?.find((e) => e.etapa.ordem === 3)?.concluidaEm;

    const selectedCategoria = useMemo(() => {
        if (!ordenacaoFontesCategorias || !selectedCategoriaId) {
            return null;
        }

        const item = ordenacaoFontesCategorias.find((e) => e.id === selectedCategoriaId);

        if (!item) {
            return null;
        }

        return { ...item };
    }, [ordenacaoFontesCategorias, selectedCategoriaId]);

    const fontesFromSelectedCategoria = useMemo(() => {
        if (!selectedCategoria) {
            if (ordenacaoFontesCategorias) {
                const allCategorias = ordenacaoFontesCategorias.reduce((prev, current) => {
                    return [...prev, ...current.fontes.map((e) => ({ ...e, categoria: current.nome, categoriaId: current.id }))];
                }, [] as IOrdenacaoFonteItem[]);

                return allCategorias;
            }

            return [];
        }

        return [...selectedCategoria.fontes];
    }, [selectedCategoria, ordenacaoFontesCategorias]);

    const categoriasDiferentesLength = useMemo(() => {
        if (selectedCategoria) {
            return null;
        }

        const IdsSet = new Set();

        fontesFromSelectedCategoria.forEach((e) => {
            IdsSet.add(e.categoriaId);
        });

        return IdsSet.size;
    }, [selectedCategoria, fontesFromSelectedCategoria]);

    const fontesFavoritas = useMemo(() => {
        if (!fontesFromSelectedCategoria) {
            return null;
        }

        return fontesFromSelectedCategoria.filter((e) => e.favorita);
    }, [fontesFromSelectedCategoria]);

    const filteredOrdenacaoFontes = useMemo(() => {
        return fontesFromSelectedCategoria?.filter((e) => removeAcento(e.numero).includes(removeAcento(searchTextFontes)) || removeAcento(e.descricao).includes(removeAcento(searchTextFontes))) || [];
    }, [fontesFromSelectedCategoria, searchTextFontes]);

    const selectedCategoriaOrfas = useMemo(() => {
        if (!ordenacaoFontesOrfasCategorias || !selectedCategoriaOrfasId) {
            return null;
        }

        const item = ordenacaoFontesOrfasCategorias.find((e) => e.id === selectedCategoriaOrfasId);

        if (!item) {
            return null;
        }

        return { ...item };
    }, [ordenacaoFontesOrfasCategorias, selectedCategoriaOrfasId]);

    const fontesFromSelectedCategoriaOrfas = useMemo(() => {
        if (!selectedCategoriaOrfas) {
            if (ordenacaoFontesOrfasCategorias) {
                const allCategorias = ordenacaoFontesOrfasCategorias.reduce((prev, current) => {
                    return [...prev, ...current.fontes.map((e) => ({ ...e, categoria: current.nome, categoriaId: current.id }))];
                }, [] as IOrdenacaoFonteItem[]);

                return allCategorias;
            }

            return [];
        }

        return [...selectedCategoriaOrfas.fontes];
    }, [selectedCategoriaOrfas, ordenacaoFontesOrfasCategorias]);

    const categoriasDiferentesLengthOrfas = useMemo(() => {
        if (selectedCategoriaOrfas) {
            return null;
        }

        const IdsSet = new Set();

        fontesFromSelectedCategoriaOrfas.forEach((e) => {
            IdsSet.add(e.categoriaId);
        });

        return IdsSet.size;
    }, [selectedCategoriaOrfas, fontesFromSelectedCategoriaOrfas]);

    const filteredOrdenacaoFontesOrfas = useMemo(() => {
        return fontesFromSelectedCategoriaOrfas?.filter((e) => removeAcento(e.numero).includes(removeAcento(searchTextFontesOrfas)) || removeAcento(e.descricao).includes(removeAcento(searchTextFontesOrfas))) || [];
    }, [fontesFromSelectedCategoriaOrfas, searchTextFontesOrfas]);

    const countFontesOrfas = useMemo(() => {
        if (!ordenacaoFontesOrfasCategorias) {
            return 0;
        }

        return ordenacaoFontesOrfasCategorias.reduce((acc, cat) => acc + (cat.fontes?.length || 0), 0);
    }, [ordenacaoFontesOrfasCategorias]);

    const handleChangeFonteFavoritaEDescricao = useCallback(async (item: IOrdenacaoFonteItem) => {
        setOrdenacaoFontesCategorias(prevState => {
            if (!prevState) {
                return null;
            }

            const newState = [...prevState];

            const categoriaIndex = newState.findIndex((e) => e.id === (selectedCategoriaId || item.categoriaId));

            const fonteIndex = newState[categoriaIndex].fontes.findIndex((e) => e.numero === item.numero);

            newState[categoriaIndex].fontes[fonteIndex].favorita = item.favorita;
            newState[categoriaIndex].fontes[fonteIndex].descricao = item.descricao;

            return newState;
        });
    }, [selectedCategoriaId]);

    const handleChangeFonteCategoria = useCallback(async (fonte: { numero: string; categoriaId: number }) => {
        setOrdenacaoFontesCategorias((prevState) => {
            if (!prevState) return null;

            const newState = [...prevState];

            let fonteItem: IOrdenacaoFonteItem | undefined;

            for (const categoria of newState) {
                const index = categoria.fontes.findIndex((f) => f.numero === fonte.numero);

                if (index !== -1) {
                    if (categoria.id === fonte.categoriaId) {
                        return prevState;
                    }

                    [fonteItem] = categoria.fontes.splice(index, 1);
                    break;
                }
            }

            if (!fonteItem) {
                return newState;
            }

            const categoriaDestino = newState.find((c) => c.id === fonte.categoriaId);

            const fontes = categoriaDestino?.fontes;

            if (fontes) {
                const indexPrimeiroNaoFavorito = fontes.findIndex((e) => !e.favorita);

                const indexInsercao = indexPrimeiroNaoFavorito === -1 ? fontes.length : indexPrimeiroNaoFavorito;

                fontes.splice(indexInsercao, 0, fonteItem);
            }

            return newState;
        });
    }, []);

    const handleContaCreated = useCallback((conta: IContaSaldoDeContas) => {
        setContas((prevState) => {
            if (!prevState) {
                return null;
            }

            const newContasData = [...prevState];

            newContasData.push(conta);

            return newContasData;
        });
    }, []);

    const handleContaUpdated = useCallback((conta: IContaSaldoDeContas) => {
        setContas((prevState) => {
            if (!prevState) {
                return null;
            }

            const newContasData = [...prevState];

            const contaIndex = newContasData.findIndex((e) => e.id === conta.id);

            if (contaIndex >= 0) {
                newContasData[contaIndex] = conta;
            }

            return newContasData;
        });
    }, []);

    const handleChangePlanilha = (info: any) => {
        let newFileList = [...info.fileList];

        newFileList = newFileList.map((file) => {
            const isLt2M = file.size / 1024 / 1024 < 2;

            if (file.type) {
                if (!isLt2M) {
                    message.error("A imagem deve ser menor que 2MB.");
                    // return; // Ignora o arquivo inválido
                    return false;
                }
                return file;
            }
            return file;
        });

        //remove os arquivos inválidos da lista
        newFileList = newFileList.filter((file) => {
            if (file !== false) {
                return file;
            }
        });

        planilhaFormRef.current.setFieldsValue({
            planilha: newFileList,
        });

        setFileList(newFileList);
    };

    const handleRemovePlanilha = () => {
        setFileList([]);
        planilhaFormRef.current.setFieldsValue({ planilha: undefined });
    }

    const onclickTable = useCallback((record: IContaSaldoDeContas, row: any): void => {
        if (record.encerradaEm) {
            return;
        }

        if (selectedRowKeys.includes(record.id)) {
            const filter = selectedRowKeys.filter((val) => val !== record.id);
            setSelectedRowKeys(filter);
        } else {
            setSelectedRowKeys([...selectedRowKeys, record.id]);
        }
    }, [selectedRowKeys]);

    const onSelectChange = useCallback((newSelectedRowKeys): void => {
        setSelectedRowKeys(newSelectedRowKeys);
    }, []);

    const handleRemoveSelectedConta = useCallback((contaId: number) => {
        setSelectedRowKeys((prevKeys) => prevKeys.filter((id) => id !== contaId));
    }, []);

    const handleCloseContaModal = useCallback(() => {
        setIsContaModalVisible(false);
        setSelectedConta(null);
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        columnWidth: '1%',
        preserveSelectedRowKeys: true,
        getCheckboxProps: (record: IContaSaldoDeContas) => ({
            disabled: !!record.encerradaEm,
        }),
    };

    const selectedContas = useMemo(() => {
        if (!contas || selectedRowKeys.length === 0) {
            return [];
        }
        return contas.filter((c) => selectedRowKeys.includes(c.id));
    }, [contas, selectedRowKeys]);

    const hasSelectedRows = useMemo(() => {
        return selectedRowKeys.length > 0;
    }, [selectedRowKeys]);

    const categoriasOptions = useMemo(() => {
        if (!ordenacaoFontesCategorias) {
            return [];
        }

        return [
            {
                label: "Todas",
                value: null,
            },
            ...ordenacaoFontesCategorias?.map((e) => ({
                label: e.nome,
                value: e.id,
            })),
        ];
    }, [ordenacaoFontesCategorias]);

    const categoriasOptionsOrfas = useMemo(() => {
        if (!ordenacaoFontesOrfasCategorias) {
            return [];
        }

        return [
            {
                label: "Todas",
                value: null,
            },
            ...ordenacaoFontesOrfasCategorias?.map((e) => ({
                label: e.nome,
                value: e.id,
            })),
        ];
    }, [ordenacaoFontesOrfasCategorias]);

    const handleChangeSelectedCategoria = useCallback((id: number) => {
        setSelectedCategoriaId(id);
    }, []);

    const handleChangeSelectedCategoriaOrfas = useCallback((id: number) => {
        setSelectedCategoriaOrfasId(id);
    }, []);

    const handleChangeDescricaoFonteOrfa = useCallback(async (item: IOrdenacaoFonteItem) => {
        setOrdenacaoFontesOrfasCategorias(prevState => {
            if (!prevState) {
                return null;
            }

            const newState = [...prevState];

            const categoriaIndex = newState.findIndex((e) => e.id === (selectedCategoriaOrfasId || item.categoriaId));
            if (categoriaIndex === -1) return newState;

            const fonteIndex = newState[categoriaIndex].fontes.findIndex((e) => e.numero === item.numero);
            if (fonteIndex === -1) return newState;

            newState[categoriaIndex].fontes[fonteIndex].descricao = item.descricao;

            return newState;
        });
    }, [selectedCategoriaOrfasId]);

    const handleChangeCategoriaFonteOrfa = useCallback(async (fonte: { numero: string; categoriaId: number }) => {
        setOrdenacaoFontesOrfasCategorias((prevState) => {
            if (!prevState) return null;

            const newState = [...prevState];

            let fonteItem: IOrdenacaoFonteItem | undefined;

            for (const categoria of newState) {
                const index = categoria.fontes.findIndex((f) => f.numero === fonte.numero);

                if (index !== -1) {
                    if (categoria.id === fonte.categoriaId) {
                        return prevState;
                    }

                    [fonteItem] = categoria.fontes.splice(index, 1);
                    break;
                }
            }

            if (!fonteItem) {
                return newState;
            }

            const categoriaDestino = newState.find((c) => c.id === fonte.categoriaId);

            if (categoriaDestino?.fontes) {
                categoriaDestino.fontes.push(fonteItem);
            }

            return newState;
        });
    }, []);

    const handleOrderChange = useCallback((categoria: IOrdenacaoCategoriaItem) => {
        setOrdenacaoFontesCategorias(prevState => {
            if (!prevState) {
                return null;
            }

            const newState = [...prevState];

            const categoriaIndex = newState.findIndex((e) => e.id === categoria.id);

            newState[categoriaIndex] = categoria;

            return newState;
        });
    }, []);

    const findDadosPlanilha = async () => {
        try {
            if (!solicitacao) {
                return;
            }

            setIsLoadingPlanilha(true);

            const contasEPlanilhaData = await onGetContasEPlanilha({
                solicitacaoId: solicitacao.id,
            });

            setPlanilha(contasEPlanilhaData.planilha);
            setContas(contasEPlanilhaData.contas);
            setIsLoadingPlanilha(false);
        } catch (error) {
            setIsLoadingPlanilha(false);
            EmitirErro(error, true, 'findDadosPlanilha_203oasdjasdno08jo12');
        }
    }

    const handleOpenContaModal = useCallback(() => {
        setIsContaModalVisible(true);
    }, []);

    const handleClickAdicionarConta = useCallback(() => {
        handleOpenContaModal();
    }, [handleOpenContaModal]);

    const handleClickEditarConta = useCallback(() => {
        if (selectedRowKeys.length === 1) {
            const contaSelecionada = contas?.find((e) => e.id === selectedRowKeys[0]);

            if (contaSelecionada) {
                setSelectedConta(contaSelecionada);
                handleOpenContaModal();
            }
        }
    }, [selectedRowKeys, contas, handleOpenContaModal]);

    const handleClickEditarFontes = useCallback(() => {
        if (selectedRowKeys.length >= 2) {
            setIsEditarFontesModalVisible(true);
        }
    }, [selectedRowKeys]);

    const handleCloseEditarFontesModal = useCallback(() => {
        setIsEditarFontesModalVisible(false);
    }, []);

    const handleSuccessEditarFontes = useCallback((updatedContas?: IContaSaldoDeContas[]) => {
        if (updatedContas && updatedContas.length > 0) {
            setContas((prevState) => {
                if (!prevState) {
                    return null;
                }

                const updatedMap = new Map<number, IContaSaldoDeContas>();
                updatedContas.forEach((conta) => updatedMap.set(conta.id, conta));

                return prevState.map((c) => (updatedMap.has(c.id) ? updatedMap.get(c.id)! : c));
            });
        } else {
            findDadosPlanilha();
        }
        setSelectedRowKeys([]);
    }, [findDadosPlanilha]);

    const handleClickUploadPlanilha = async () => {
        try {
            const planilha = planilhaFormRef.current.getFieldValue("planilha");

            if (!planilha || !planilha.length || !solicitacao) {
                return;
            }

            setIsSubmitingPlanilha(true);

            const formData = new FormData();

            formData.append('planilha', planilha[0].originFileObj);

            await SolicitacoesDeAtivacaoService.uploadPlanilhaDeContas({
                solicitacaoId: solicitacao?.id,
                formData,
            });

            notification.success({
                message: 'Sucesso',
                description:
                    'Planilha importada com sucesso!',
            });

            handleRemovePlanilha();

            setIsSubmitingPlanilha(false);
            findDadosPlanilha();
        } catch (error) {
            setIsSubmitingPlanilha(false);
            EmitirErro(error, true, 'uploadPlanilhaContas_asd80asiojnd89123jn12');
        }
    }

    const handleDeleteConta = useCallback(async () => {
        if (selectedRowKeys.length === 1) {
            const contaSelecionada = contas?.find((e) => e.id === selectedRowKeys[0]);

            if (solicitacao && contaSelecionada) {
                try {
                    setIsDeletingConta(true);
                    await onDeleteConta({
                        contaId: contaSelecionada.id,
                        solicitacaoId: solicitacao.id
                    });

                    setContas((prevState) => {
                        if (!prevState) {
                            return null;
                        }

                        const newContasData = [...prevState];

                        const filteredContas = newContasData.filter((e) => e.id !== contaSelecionada.id);

                        return filteredContas;
                    });

                    notification.success({
                        message: 'Sucesso',
                        description: 'Conta deletada com sucesso!',
                    });

                    setSelectedRowKeys([]);
                } catch (error) {
                    EmitirErro(error, true, 'deleteConta_2304onasdasjnod023kmopkas');
                } finally {
                    setIsDeletingConta(false);
                }
            }
        }
    }, [selectedRowKeys, solicitacao, contas, onDeleteConta]);

    const handleEncerrarConta = useCallback(async () => {
        if (selectedRowKeys.length === 1) {
            const contaSelecionada = contas?.find((e) => e.id === selectedRowKeys[0]);

            if (contaSelecionada) {
                try {
                    setIsEncerrandoConta(true);
                    if (onEncerrarConta) {
                        await onEncerrarConta(contaSelecionada.id);
                    } else {
                        await SolicitacoesDeAtivacaoService.encerrarConta(contaSelecionada.id);
                    }

                    notification.success({
                        message: 'Sucesso',
                        description: 'Conta encerrada com sucesso!',
                    });

                    setSelectedRowKeys([]);
                    await findDadosPlanilha();
                } catch (error) {
                    EmitirErro(error, true, 'encerrarConta_2304onasdasjnod023kmopkas');
                } finally {
                    setIsEncerrandoConta(false);
                }
            }
        }
    }, [selectedRowKeys, contas, onEncerrarConta, findDadosPlanilha]);

    const handleDeletePlanilha = useCallback(async () => {
        if (solicitacao) {
            try {
                setIsDeletingPlanilha(true);

                const updatedSolicitacao = await SolicitacoesDeAtivacaoService.deletePlanilha({
                    solicitacaoId: solicitacao.id
                });

                notification.success({
                    message: 'Sucesso',
                    description: 'Planilha deletada com sucesso!',
                });

                setOrdenacaoFontesCategorias(null);
                setSelectedCategoriaId(null);
                setPlanilha(null);
                setContas(null);

                setIsDeletingPlanilha(false);
                onEtapaIniciada(updatedSolicitacao);
                findDadosPlanilha();
            } catch (error) {
                EmitirErro(error, true, 'deletePlanilha_2304onasdasjnod023kmopkas');
                setIsDeletingPlanilha(false);
            }
        }
    }, [solicitacao, onEtapaIniciada]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
    }, []);

    const handleSearch = useCallback((selectedKeys, confirm, dataIndex): void => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    }, []);

    const handleReset = useCallback((clearFilters): void => {
        clearFilters();
        setSearchText('');
    }, []);

    const columnSearchPropsFontes = useFontesColumnSearchProps();

    const columnSearchPropsOrgao = useMemo(() => {
        let searchInput: any = null;
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div
                    style={{ padding: 8 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                            handleSearch(selectedKeys, confirm, 'orgaoVinculado');
                        }
                    }}
                >
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder="Pesquisar órgão ou CNPJ..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, 'orgaoVinculado')}
                        style={{ width: 220, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, 'orgaoVinculado')}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 95 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 95 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                if (!value) return true;
                const term = removeAcento(value.toString()).toLowerCase().trim();
                const words = term.split(/\s+/).filter(Boolean);
                const termDigits = onlyNumbers(value.toString());

                const orgaoNome = removeAcento(record.orgaoVinculado?.nome || '').toLowerCase();
                const cnpj = record.orgaoVinculado?.cnpj || '';
                const cnpjDigits = onlyNumbers(cnpj);
                const cnpjMasked = mask(cnpj, ["99.999.999/9999-99"]).toLowerCase();

                const matchNome =
                    orgaoNome.includes(term) ||
                    (words.length > 1 && words.every(w => orgaoNome.includes(w)));
                const matchCnpj =
                    (termDigits.length > 0 && cnpjDigits.includes(termDigits)) ||
                    cnpjMasked.includes(term) ||
                    cnpj.toLowerCase().includes(term);

                return matchNome || matchCnpj;
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput?.select());
                }
            },
        };
    }, [handleSearch, handleReset]);

    const columnSearchPropsContaBancaria = useMemo(() => {
        let searchInput: any = null;
        return {
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div
                    style={{ padding: 8 }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.stopPropagation();
                            handleSearch(selectedKeys, confirm, 'contaBancaria');
                        }
                    }}
                >
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder="Pesquisar agência, conta ou banco..."
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, 'contaBancaria')}
                        style={{ width: 230, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, 'contaBancaria')}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 95 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 95 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => (
                <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
            ),
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                if (!value) return true;
                const rawValue = value.toString();
                const term = removeAcento(rawValue).toLowerCase().trim();
                const words = term.split(/\s+/).filter(Boolean);
                const termDigits = onlyNumbers(rawValue);
                const termAlphanumeric = onlyNumbersAndLetters(rawValue).toLowerCase();

                const bancoNome = removeAcento(record.banco?.nome || '').toLowerCase();
                const bancoSigla = removeAcento(record.banco?.sigla || '').toLowerCase();
                const agencia = removeAcento(record.agencia || '').toLowerCase();
                const agenciaDigits = onlyNumbers(record.agencia || '');
                const numero = removeAcento(record.numero || '').toLowerCase();
                const numeroDigits = onlyNumbers(record.numero || '');
                const numeroAlphanumeric = onlyNumbersAndLetters(record.numero || '').toLowerCase();
                const agContaText = removeAcento(`ag: ${record.agencia || ''} conta: ${record.numero || ''}`).toLowerCase();
                const fullText = `${bancoNome} ${bancoSigla} ${agencia} ${numero} ${agContaText}`;

                const matchBanco =
                    bancoNome.includes(term) ||
                    bancoSigla.includes(term) ||
                    (words.length > 1 && words.every(w => bancoNome.includes(w) || bancoSigla.includes(w)));
                const matchAgencia =
                    agencia.includes(term) ||
                    (termDigits.length > 0 && agenciaDigits.includes(termDigits));
                const matchNumero =
                    numero.includes(term) ||
                    (termDigits.length > 0 && numeroDigits.includes(termDigits)) ||
                    (termAlphanumeric.length > 0 && numeroAlphanumeric.includes(termAlphanumeric));
                const matchAgConta =
                    agContaText.includes(term) ||
                    (words.length > 1 && words.every(w => fullText.includes(w)));

                return matchBanco || matchAgencia || matchNumero || matchAgConta;
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput?.select());
                }
            },
        };
    }, [handleSearch, handleReset]);

    const handleClearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setSearchText('');
        setSearchedColumn('');
        refContagemDoFiltro.current = [];
    }, []);

    const hasActiveFilters = useMemo(() => {
        return checkHasActiveFilters(filteredInfo, searchText);
    }, [filteredInfo, searchText]);

    const getColumnSearchProps = useCallback((
        dataIndex,
        valueFormatterBeforeCompare?: (value: string) => string,
    ) => {
        let searchInput: any = '';
        return ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
                <div style={{ padding: 8 }}>
                    <Input
                        ref={node => {
                            searchInput = node;
                        }}
                        placeholder={`Digite...`}
                        value={selectedKeys[0]}
                        onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        style={{ width: 188, marginBottom: 8, display: 'block' }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: any) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
            onFilter: (value: any, record: any) => {

                const recordValue = record[dataIndex]?.toString();

                let trecord = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(recordValue) : recordValue);

                const valueToCompare = removeAcento(valueFormatterBeforeCompare ? valueFormatterBeforeCompare(value) : value);

                return record[dataIndex] ? trecord.includes(valueToCompare) : false
            },
            onFilterDropdownVisibleChange: (visible: any) => {
                if (visible) {
                    setTimeout(() => searchInput.select());
                }
            },
            render: (text: any) =>
                searchedColumn === dataIndex ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={text ? text.toString() : ''}
                    />
                ) : (
                    text
                ),
        })
    }, [searchText, searchedColumn, handleSearch, handleReset]);

    const columns = [
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            key: 'descricao',
            width: '14%',
            filteredValue: filteredInfo.descricao || null,
            ...getColumnSearchProps('descricao'),
        },
        {
            title: 'Órgão',
            dataIndex: 'orgaoVinculado',
            key: 'orgaoVinculado',
            filteredValue: filteredInfo.orgaoVinculado || null,
            ...columnSearchPropsOrgao,
            width: '18%',
            render: (_: any, record: IContaSaldoDeContas) => {
                const maskedCnpj = record.orgaoVinculado?.cnpj
                    ? mask(record.orgaoVinculado.cnpj, ["99.999.999/9999-99"])
                    : '-';

                const isSearched = searchedColumn === 'orgaoVinculado' && !!searchText;
                const searchWords = isSearched
                    ? [searchText, ...searchText.split(/\s+/).filter(Boolean), onlyNumbers(searchText)].filter(Boolean)
                    : [];

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                        <span style={{ fontWeight: 500 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={record.orgaoVinculado?.nome || '-'}
                                />
                            ) : (
                                record.orgaoVinculado?.nome || '-'
                            )}
                        </span>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={maskedCnpj}
                                />
                            ) : (
                                maskedCnpj
                            )}
                        </Typography.Text>
                    </div>
                );
            }
        },
        {
            title: 'Conta bancária',
            dataIndex: 'contaBancaria',
            key: 'contaBancaria',
            filteredValue: filteredInfo.contaBancaria || null,
            ...columnSearchPropsContaBancaria,
            width: '18%',
            render: (_: any, record: IContaSaldoDeContas) => {
                const agContaText = `Ag: ${record.agencia || '-'} | Conta: ${record.numero || '-'}`;
                const bancoNome = record.banco?.nome || '-';

                const isSearched = searchedColumn === 'contaBancaria' && !!searchText;
                const searchWords = isSearched
                    ? [searchText, ...searchText.split(/\s+/).filter(Boolean), onlyNumbers(searchText)].filter(Boolean)
                    : [];

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                        <span style={{ fontWeight: 500 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={agContaText}
                                />
                            ) : (
                                agContaText
                            )}
                        </span>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            {isSearched ? (
                                <Highlighter
                                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                    searchWords={searchWords}
                                    autoEscape
                                    textToHighlight={bancoNome}
                                />
                            ) : (
                                bancoNome
                            )}
                        </Typography.Text>
                    </div>
                );
            }
        },
        {
            title: 'Fontes',
            dataIndex: 'fontesConta',
            key: 'fontesConta',
            filteredValue: filteredInfo.fontesConta || null,
            ...columnSearchPropsFontes,
            width: '18%',
            render: (fontes: IFonteVinculadaSaldoDeContas[]) => (
                <FontesTagsCell fontes={fontes} />
            )
        },
        {
            title: 'Planilha',
            dataIndex: 'planilha',
            key: 'planilha',
            filteredValue: filteredInfo.planilha || null,
            width: '9%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                return value ? !!record.planilha : !record.planilha;
            },
            render: (planilha: { id: number } | null) => !!planilha ? "Sim" : "Não"
        },
        {
            title: 'Ativa',
            dataIndex: 'ativa',
            key: 'ativa',
            filteredValue: filteredInfo.ativa || null,
            width: '9%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                return value ? record.ativa : !record.ativa;
            },
            render: (ativa: boolean) => ativa ? "Sim" : "Não"
        },
        {
            title: 'Encerrada',
            dataIndex: 'encerradaEm',
            key: 'encerradaEm',
            filteredValue: filteredInfo.encerradaEm || null,
            width: '9%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                return value ? !!record.encerradaEm : !record.encerradaEm;
            },
            sorter: (a: IContaSaldoDeContas, b: IContaSaldoDeContas) =>
                (a.encerradaEm ? moment(a.encerradaEm).unix() : 0) -
                (b.encerradaEm ? moment(b.encerradaEm).unix() : 0),
            render: (encerradaEm: string | null) => {
                if (!encerradaEm) {
                    return "Não";
                }

                return (
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                        <span>Sim</span>
                        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                            {moment(encerradaEm).format('DD/MM/YYYY HH:mm')}
                        </Typography.Text>
                    </div>
                );
            },
        },
        {
            title: 'Integrada',
            dataIndex: 'integrada',
            key: 'integrada',
            filteredValue: filteredInfo.integrada || null,
            width: '5%',
            filters: [
                {
                    text: 'Sim',
                    value: true,
                },
                {
                    text: 'Não',
                    value: false,
                },
            ],
            onFilter: (value: any, record: IContaSaldoDeContas) => {
                return value ? record.integrada : !record.integrada;
            },
            render: (integrada: boolean) => integrada ? <FaCheckCircle color="#00b153" size={20} /> : <FaTimesCircle color="#ff4747" size={20} />
        },
    ];

    const downloadPlanilha = (url: string) => {
        fetch(url)
            .then((response) => response.blob())
            .then((blob) => {
                const element = document.createElement("a");
                element.href = URL.createObjectURL(blob);
                element.download = url.split("/").pop() || "download";
                element.click();
            })
            .catch((error) => {
                window.open(url);
            });
    };

    const reloadFontesConsolidadas = useCallback(async () => {
        if (!solicitacao) {
            return;
        }

        try {
            setIsLoadingOrdenacaoFontes(true);

            const ordenacaoFontesData = await onGetOrdenacaoFontes({
                solicitacaoId: solicitacao.id,
            });

            setOrdenacaoFontesCategorias(ordenacaoFontesData);
            if (selectedCategoriaId && !ordenacaoFontesData?.some((c) => c.id === selectedCategoriaId)) {
                setSelectedCategoriaId(null);
            }
        } catch (error) {
            EmitirErro(error, true, 'getOrdenacaoFontes_asdapm-9213m');
        } finally {
            setIsLoadingOrdenacaoFontes(false);
        }
    }, [solicitacao, onGetOrdenacaoFontes, selectedCategoriaId]);

    const reloadFontesOrfas = useCallback(async () => {
        if (!solicitacao || !onGetFontesOrfas) {
            return;
        }

        try {
            setIsLoadingFontesOrfas(true);

            const fontesOrfasData = await onGetFontesOrfas({
                solicitacaoId: solicitacao.id,
            });

            setOrdenacaoFontesOrfasCategorias(fontesOrfasData);
            if (selectedCategoriaOrfasId && !fontesOrfasData?.some((c) => c.id === selectedCategoriaOrfasId)) {
                setSelectedCategoriaOrfasId(null);
            }
        } catch (error) {
            EmitirErro(error, true, 'getFontesOrfas_asdapm-9213m');
        } finally {
            setIsLoadingFontesOrfas(false);
        }
    }, [solicitacao, onGetFontesOrfas, selectedCategoriaOrfasId]);

    const handleDeleteFonte = useCallback(async (fonteId: number) => {
        if (!onDeleteFonte) {
            return;
        }

        try {
            await onDeleteFonte({
                fonteId,
                solicitacaoId: solicitacao?.id,
            });

            notification.success({
                message: 'Sucesso',
                description: 'Fonte deletada com sucesso!',
            });

            await Promise.all([
                reloadFontesConsolidadas(),
                reloadFontesOrfas(),
            ]);
        } catch (error: any) {
            if (typeof error?.response?.data === 'string') {
                notification.error({
                    message: 'Erro',
                    description: error.response.data,
                });
            } else {
                EmitirErro(error, true, 'deleteFonte_asd98123mn');
            }
            throw error;
        }
    }, [onDeleteFonte, solicitacao, reloadFontesConsolidadas, reloadFontesOrfas]);

    const handleChangeTab = useCallback(async (tabKey) => {
        setActiveTabKey(tabKey);

        if (tabKey === 'fontes' && solicitacao) {
            await reloadFontesConsolidadas();
        }

        if (tabKey === 'fontes-orfas' && solicitacao) {
            await reloadFontesOrfas();
        }

        if (tabKey === 'contas' && solicitacao) {
            if (recarregarContas) {
                findDadosPlanilha();
                onContasRecarregadas?.();
            }
        }
    }, [solicitacao, reloadFontesConsolidadas, reloadFontesOrfas, recarregarContas, onContasRecarregadas]);

    useEffect(() => {
        findDadosPlanilha();
    }, []);

    useEffect(() => {
        if (solicitacao && onGetFontesOrfas) {
            reloadFontesOrfas();
        }
    }, [solicitacao, onGetFontesOrfas, reloadFontesOrfas]);

    useEffect(() => {
        if (recarregarContas && etapaAtiva && activeTabKey === 'contas' && solicitacao) {
            findDadosPlanilha();
            onContasRecarregadas?.();
        }
    }, [recarregarContas, etapaAtiva, activeTabKey, solicitacao, onContasRecarregadas]);

    const items = useMemo(() => {
        if (!solicitacao) {
            return;
        }

        return [
            {
                key: 'planilha',
                label: 'Planilha',
                children: (
                    <Spin
                        spinning={isLoadingPlanilha}
                        style={isLoadingPlanilha ? {
                            marginTop: 15
                        } : undefined}
                    >
                        {
                            !isLoadingPlanilha && !planilha && (
                                <Col span={24}>
                                    <Form
                                        ref={planilhaFormRef}
                                        name="formPlanilhaSaldoDeContas"
                                        layout="vertical"
                                        initialValues={{
                                            remember: true,
                                        }}
                                    >
                                        <Form.Item
                                            rules={
                                                [{
                                                    required: true,
                                                    message: "Por favor envie a planilha de contas!",
                                                }]}
                                        >
                                            {
                                                fileList.length === 0 && (
                                                    <Dragger
                                                        multiple={false}
                                                        accept=".xlsx"
                                                        onChange={handleChangePlanilha}
                                                        fileList={fileList}
                                                        name="file"
                                                        // tira a lista de imagens
                                                        customRequest={() => { }}
                                                        showUploadList={false}
                                                        style={{
                                                            marginBottom: 10,
                                                        }}
                                                    >
                                                        <p className="ant-upload-drag-icon">
                                                            <InboxOutlined />
                                                        </p>
                                                        <p className="ant-upload-text">
                                                            Clique ou arraste o arquivo para esta
                                                            área para fazer upload
                                                        </p>
                                                    </Dragger>
                                                )
                                            }

                                            {fileList.map((file: any, index: any) => (
                                                <div
                                                    key={index}
                                                    className="image-container card-border"
                                                    style={{
                                                        display: "flex",
                                                        alignItems: 'center',
                                                        width: 'fit-content',
                                                        marginRight: 10,
                                                        padding: 4,
                                                        paddingRight: 8,
                                                        borderRadius: 4
                                                    }}
                                                >
                                                    <BiFile size={24} color="#54c550" />

                                                    <Typography.Text>{file.name}</Typography.Text>

                                                    <div key={Math.random()} className="image-icons" >
                                                        <a
                                                            className="download-icon"
                                                            onClick={() => downloadPlanilha(URL.createObjectURL(file.originFileObj))}
                                                            download
                                                        >
                                                            <BiDownload />
                                                        </a>

                                                        <span
                                                            className="edit-icon"
                                                            onClick={handleRemovePlanilha}
                                                        >
                                                            <BiTrash />
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </Form.Item>

                                        <Row style={{ justifyContent: 'flex-end', width: '100%' }} >
                                            <Button
                                                type="primary"
                                                onClick={handleClickUploadPlanilha}
                                                disabled={isSubmitingPlanilha}
                                                loading={isSubmitingPlanilha}
                                            >
                                                {'Enviar planilha'}
                                            </Button>
                                        </Row>
                                    </Form>
                                </Col>
                            )
                        }

                        {
                            planilha && (
                                <Col span={24}>
                                    <Row gutter={[12, 12]}>
                                        <Col
                                            xs={24}
                                            sm={24}
                                            md={24}
                                            lg={24}
                                            xl={24}
                                            span={24}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    borderRadius: 8,
                                                    border: `1px solid ${propsState.theme === 'light' ? '#d9d9d9' : '#434343'}`,
                                                    gap: 12,
                                                    overflow: 'hidden',
                                                    paddingRight: 12,
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 16,
                                                        padding: '8px 0',
                                                        paddingLeft: 12,
                                                        alignSelf: 'stretch',
                                                        borderLeft: '3px solid #00B153'
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            padding: 12,
                                                            backgroundColor: propsState.theme === 'light' ? '#d6f9e6' : '#18432E',
                                                            borderRadius: 8,
                                                        }}
                                                    >
                                                        <FaRegFileExcel
                                                            color="#00B153"
                                                            size={24}
                                                        />
                                                    </div>

                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                        }}
                                                    >
                                                        <Typography.Text strong >
                                                            {planilha.originalName}
                                                        </Typography.Text>

                                                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                                            Importada em {moment(planilha.createdAt).format('DD/MM/YYYY HH:mm')}
                                                        </Typography.Text>
                                                    </div>
                                                </div>

                                                {
                                                    !isManutencao && !etapa3ConcluidaEm && (
                                                        <div>
                                                            <Popconfirm
                                                                title="Deseja realmente deletar a planilha?"
                                                                onConfirm={handleDeletePlanilha}
                                                                onCancel={() => { }}
                                                                okText="Sim"
                                                                cancelText="Não"
                                                                okButtonProps={{
                                                                    loading: isDeletingPlanilha,
                                                                    disabled: isDeletingPlanilha,
                                                                }}
                                                                cancelButtonProps={{
                                                                    disabled: isDeletingPlanilha
                                                                }}
                                                                disabled={isDeletingPlanilha}
                                                            >
                                                                <Button
                                                                    danger
                                                                    ghost
                                                                    loading={isDeletingPlanilha}
                                                                    disabled={isDeletingPlanilha}
                                                                >
                                                                    <DeleteOutlined />
                                                                </Button>
                                                            </Popconfirm>
                                                        </div>
                                                    )
                                                }
                                            </div>
                                        </Col>
                                    </Row>
                                </Col>
                            )
                        }
                    </Spin>
                )
            },
            {
                key: 'fontes',
                label: 'Fontes',
                disabled: !planilha,
                children: (
                    <Spin spinning={isLoadingOrdenacaoFontes} >
                        <Col span={24}>
                            <Row gutter={[12, 12]}>
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                >
                                    <div
                                        style={{
                                            height: 400,
                                            border: `1px solid ${propsState.theme === 'light' ? '#d9d9d9' : '#434343'}`,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                padding: '6px 16px',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Select
                                                className="rounded-select"
                                                onChange={handleChangeSelectedCategoria}
                                                value={selectedCategoriaId}
                                                options={categoriasOptions}
                                                style={{
                                                    width: '100%',
                                                    marginBottom: 8,
                                                    marginTop: 4,
                                                }}
                                            />

                                            <Input
                                                placeholder="Buscar..."
                                                onChange={(e) => setSearchTextFontes(e.target.value)}
                                                prefix={<SearchOutlined style={{ marginRight: 4 }} />}
                                                allowClear
                                                style={{
                                                    borderRadius: 8,
                                                }}
                                            />

                                            <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
                                                {filteredOrdenacaoFontes?.length} de {fontesFromSelectedCategoria?.length} fontes{!selectedCategoria ? ` • ${categoriasDiferentesLength} categorias` : ''} • {fontesFavoritas?.length} favoritas
                                            </Typography.Text>
                                        </div>

                                        <Divider
                                            style={{
                                                margin: 0,
                                                marginTop: 6,
                                                flexShrink: 0,
                                            }}
                                        />

                                        <div
                                            style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                minHeight: 0,
                                            }}
                                        >
                                            {filteredOrdenacaoFontes?.map((e, index) => (
                                                <Fragment key={`${e.numero}-${e.favorita}`}>
                                                    <OrdenacaoFonteItem
                                                        item={e}
                                                        solicitacaoId={solicitacao.id}
                                                        onChangeFavorita={handleChangeFonteFavoritaEDescricao}
                                                        selectedCategoriaId={selectedCategoriaId}
                                                        onUpdateFonteFavorita={onUpdateFonteFavorita}
                                                        fonteActionsProps={{
                                                            onChangeDescricao: handleChangeFonteFavoritaEDescricao,
                                                            onOnlyCategoriaUpdated: handleChangeFonteCategoria,
                                                            categoriaId: selectedCategoriaId || e.categoriaId || null,
                                                            onUpdateCategoriaFonte,
                                                            onUpdateDescricaoFonte,
                                                            onDeleteFonte: handleDeleteFonte,
                                                        }}
                                                    />

                                                    {index < filteredOrdenacaoFontes.length - 1 && (
                                                        <Divider style={{ margin: 0 }} />
                                                    )}
                                                </Fragment>
                                            ))}
                                        </div>
                                    </div>
                                </Col>

                                <Col
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                >
                                    <div
                                        style={{
                                            height: 400,
                                            border: `1px solid ${propsState.theme === 'light' ? '#d9d9d9' : '#434343'}`,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '6px 16px',
                                                gap: 8,
                                                flexShrink: 0,
                                            }}
                                        >
                                            <FaStar
                                                size={16}
                                                color="#fe9a00"
                                            />

                                            <Typography.Title
                                                level={5}
                                                style={{ marginBottom: 0 }}
                                            >
                                                Favoritas {selectedCategoria ? `(${fontesFavoritas?.length})` : ''}
                                            </Typography.Title>
                                        </div>

                                        <Divider
                                            style={{
                                                margin: 0,
                                                marginTop: 6,
                                                flexShrink: 0,
                                            }}
                                        />

                                        {
                                            fontesFavoritas?.length === 0 && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        margin: '0 16px',
                                                        marginTop: 12
                                                    }}
                                                >
                                                    <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
                                                        Não há fontes favoritas nessa categoria
                                                    </Typography.Text>
                                                </div>
                                            )
                                        }

                                        {
                                            !selectedCategoria && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        margin: '0 16px',
                                                        marginTop: 12,
                                                    }}
                                                >
                                                    <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
                                                        Selecione uma categoria para poder ordenar as fontes favoritas
                                                    </Typography.Text>
                                                </div>
                                            )
                                        }

                                        <div
                                            style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                minHeight: 0,
                                            }}
                                        >
                                            {fontesFavoritas && selectedCategoria && (
                                                <OrdenacaoList
                                                    favoritas={fontesFavoritas}
                                                    onRemoveFavorita={handleChangeFonteFavoritaEDescricao}
                                                    solicitacaoId={solicitacao.id}
                                                    categoriaId={selectedCategoriaId}
                                                    onOrderChange={handleOrderChange}
                                                    onUpdateFonteFavorita={onUpdateFonteFavorita}
                                                    onUpdateOrdenacaoFontes={onUpdateOrdenacaoFontes}
                                                    fonteActionsProps={{
                                                        onChangeDescricao: handleChangeFonteFavoritaEDescricao,
                                                        onOnlyCategoriaUpdated: handleChangeFonteCategoria,
                                                        categoriaId: selectedCategoriaId,
                                                        onUpdateCategoriaFonte,
                                                        onUpdateDescricaoFonte,
                                                        onDeleteFonte: handleDeleteFonte,
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Spin>
                )
            },
            {
                key: 'fontes-orfas',
                label: (
                    <TabTitleCount
                        descricao="Fontes órfãs"
                        count={countFontesOrfas}
                        badgeBackgroundColor="#EB5757"
                        badgeTextColor="#FFFFFF"
                    />
                ),
                disabled: !planilha,
                children: (
                    <Spin spinning={isLoadingFontesOrfas} >
                        <Col span={24}>
                            <Row gutter={[12, 12]}>
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={12}
                                    lg={12}
                                    xl={12}
                                >
                                    <div
                                        style={{
                                            height: 400,
                                            border: `1px solid ${propsState.theme === 'light' ? '#d9d9d9' : '#434343'}`,
                                            borderRadius: 8,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <div
                                            style={{
                                                padding: '6px 16px',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <Select
                                                className="rounded-select"
                                                onChange={handleChangeSelectedCategoriaOrfas}
                                                value={selectedCategoriaOrfasId}
                                                options={categoriasOptionsOrfas}
                                                style={{
                                                    width: '100%',
                                                    marginBottom: 8,
                                                    marginTop: 4,
                                                }}
                                            />

                                            <Input
                                                placeholder="Buscar..."
                                                onChange={(e) => setSearchTextFontesOrfas(e.target.value)}
                                                prefix={<SearchOutlined style={{ marginRight: 4 }} />}
                                                allowClear
                                                style={{
                                                    borderRadius: 8,
                                                }}
                                            />

                                            <Typography.Text type="secondary" style={{ fontSize: '0.75rem' }}>
                                                {filteredOrdenacaoFontesOrfas?.length} de {fontesFromSelectedCategoriaOrfas?.length} fontes órfãs{!selectedCategoriaOrfas ? ` • ${categoriasDiferentesLengthOrfas} categorias` : ''}
                                            </Typography.Text>
                                        </div>

                                        <Divider
                                            style={{
                                                margin: 0,
                                                marginTop: 6,
                                                flexShrink: 0,
                                            }}
                                        />

                                        <div
                                            style={{
                                                flex: 1,
                                                overflowY: 'auto',
                                                minHeight: 0,
                                            }}
                                        >
                                            {filteredOrdenacaoFontesOrfas?.map((e, index) => (
                                                <Fragment key={`${e.numero}-${e.id || index}`}>
                                                    <OrdenacaoFonteItem
                                                        item={e}
                                                        solicitacaoId={solicitacao.id}
                                                        selectedCategoriaId={selectedCategoriaOrfasId}
                                                        fonteActionsProps={{
                                                            onChangeDescricao: handleChangeDescricaoFonteOrfa,
                                                            onOnlyCategoriaUpdated: handleChangeCategoriaFonteOrfa,
                                                            categoriaId: selectedCategoriaOrfasId || e.categoriaId || null,
                                                            onUpdateCategoriaFonte,
                                                            onUpdateDescricaoFonte,
                                                            onDeleteFonte: handleDeleteFonte,
                                                            hideFavorite: true,
                                                            isOrfaTab: true,
                                                        }}
                                                    />

                                                    {index < filteredOrdenacaoFontesOrfas.length - 1 && (
                                                        <Divider style={{ margin: 0 }} />
                                                    )}
                                                </Fragment>
                                            ))}

                                            {filteredOrdenacaoFontesOrfas?.length === 0 && (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        margin: '0 16px',
                                                        marginTop: 24,
                                                    }}
                                                >
                                                    <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
                                                        {fontesFromSelectedCategoriaOrfas?.length === 0
                                                            ? 'Não há fontes órfãs'
                                                            : 'Nenhuma fonte órfã encontrada'}
                                                    </Typography.Text>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Spin>
                )
            },
            {
                key: 'contas',
                label: 'Contas',
                disabled: !planilha,
                children: (
                    <>
                        <Col span={24}>
                            <div
                                style={{
                                    position: 'sticky',
                                    top: 84,
                                    zIndex: 2,
                                    width: 'fit-content'
                                }}
                            >
                                <Row style={{ marginBottom: 4 }}>
                                    <Col>
                                        <Button
                                            type="primary"
                                            disabled={selectedRowKeys.length > 0}
                                            onClick={handleClickAdicionarConta}
                                            className="botaoAdicionarAssociacao"
                                        >
                                            Adicionar
                                        </Button>
                                    </Col>

                                    <Col>
                                        <Button
                                            type="primary"
                                            onClick={selectedRowKeys.length >= 2 ? handleClickEditarFontes : handleClickEditarConta}
                                            disabled={selectedRowKeys.length === 0}
                                            className="botaoEditarAssociacao"
                                            style={{
                                                width: 'auto',
                                                minWidth: 90,
                                                padding: '0 14px',
                                                marginRight: 5,
                                                marginLeft: 5,
                                                marginBottom: 5,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                <span>Editar</span>
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        maxWidth: selectedRowKeys.length >= 2 ? 60 : 0,
                                                        opacity: selectedRowKeys.length >= 2 ? 1 : 0,
                                                        transform: selectedRowKeys.length >= 2 ? 'translateX(0)' : 'translateX(-6px)',
                                                        overflow: 'hidden',
                                                        whiteSpace: 'nowrap',
                                                        transition: 'max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease, transform 0.25s ease',
                                                    }}
                                                >
                                                    &nbsp;fontes
                                                </span>
                                            </span>

                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    maxWidth: selectedRowKeys.length >= 2 ? 40 : 0,
                                                    opacity: selectedRowKeys.length >= 2 ? 1 : 0,
                                                    transform: selectedRowKeys.length >= 2 ? 'scale(1)' : 'scale(0.5)',
                                                    marginLeft: selectedRowKeys.length >= 2 ? 6 : 0,
                                                    padding: selectedRowKeys.length >= 2 ? '0 6px' : '0',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    backgroundColor: 'rgba(255, 255, 255, 0.35)',
                                                    color: '#fff',
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    borderRadius: 10,
                                                    height: 18,
                                                    lineHeight: '18px',
                                                    transition: 'max-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease, transform 0.25s ease, margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                }}
                                            >
                                                {selectedRowKeys.length}
                                            </span>
                                        </Button>
                                    </Col>

                                    <Col>
                                        <Popconfirm
                                            title={
                                                <div style={{ maxWidth: 260 }}>
                                                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Deseja encerrar esta conta?</div>
                                                    <div style={{ fontSize: 12 }}>
                                                        Esta ação é definitiva e não há possibilidade de reabrir a conta.
                                                    </div>
                                                </div>
                                            }
                                            onConfirm={handleEncerrarConta}
                                            onCancel={() => { }}
                                            okText="Sim, encerrar"
                                            cancelText="Cancelar"
                                            placement="bottom"
                                            okButtonProps={{ danger: true, loading: isEncerrandoConta }}
                                            disabled={selectedRowKeys.length !== 1 || isEncerrandoConta}
                                        >
                                            <Button
                                                type="primary"
                                                danger
                                                disabled={selectedRowKeys.length !== 1}
                                                loading={isEncerrandoConta}
                                                style={{ marginRight: 5, marginBottom: 5 }}
                                            >
                                                Encerrar
                                            </Button>
                                        </Popconfirm>
                                    </Col>

                                    <Col>
                                        <Popconfirm
                                            title="Deseja realmente deletar a conta?"
                                            onConfirm={handleDeleteConta}
                                            onCancel={() => { }}
                                            okText="Sim, deletar"
                                            cancelText="Cancelar"
                                            placement="bottom"
                                            okButtonProps={{ danger: true, loading: isDeletingConta }}
                                            disabled={selectedRowKeys.length !== 1 || isDeletingConta}
                                        >
                                            <Button
                                                type="primary"
                                                danger
                                                disabled={selectedRowKeys.length !== 1}
                                                loading={isDeletingConta}
                                                style={{ marginRight: 5, marginBottom: 5 }}
                                            >
                                                <DeleteOutlined />
                                            </Button>
                                        </Popconfirm>
                                    </Col>

                                    <Col>
                                        <BotaoLimparFiltros
                                            hasActiveFilters={hasActiveFilters}
                                            onClick={handleClearAllFilters}
                                        />
                                    </Col>

                                    <Col>
                                        <BotaoLimparSelecao
                                            hasSelectedRows={selectedRowKeys.length > 0}
                                            onClick={handleResetRowSelection}
                                        />
                                    </Col>
                                </Row>
                            </div>

                            <div className='headerAbaixodosBtnTableList_ihabdfvohewvbfopu' />

                            <Table
                                rowKey={(record) => record.id}
                                onRow={(record, rowIndex) => {
                                    return {
                                        onClick: event => { onclickTable(record, rowIndex) },
                                        style: {
                                            cursor: record.encerradaEm ? 'not-allowed' : 'pointer',
                                        },
                                    };
                                }}
                                title={(registros) => {

                                    return (
                                        <div
                                            style={{
                                                backgroundColor: propsState.theme === 'light' ? '#FAFAFA' : '#1D1D1D',
                                                padding: 10,
                                                margin: -8,
                                                marginBottom: 0.5
                                            }}
                                        >
                                            <Typography.Text>
                                                {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0 ?
                                                    contas?.length :
                                                    refContagemDoFiltro.current.length} num total de ${contas?.length} `}
                                            </Typography.Text>
                                        </div>
                                    )
                                }}
                                footer={(registros) => {
                                    return (
                                        <div
                                            style={{
                                                textAlign: 'end'
                                            }}
                                        >
                                            <Typography.Text>
                                                {`Mostrando: ${registros.length} de ${refContagemDoFiltro.current.length === 0 ?
                                                    contas?.length :
                                                    refContagemDoFiltro.current.length} num total de ${contas?.length} `}
                                            </Typography.Text>
                                        </div>
                                    )
                                }}
                                rowSelection={rowSelection}
                                style={{ marginTop: -34 }}
                                size="small"
                                columns={columns}
                                dataSource={contas || []}
                                pagination={{ locale: { items_per_page: '' } }}
                                onChange={
                                    (pagination, filters, sorter, extra) => {
                                        setFilteredInfo(filters || {});
                                        refContagemDoFiltro.current = extra.currentDataSource
                                    }
                                }
                            />
                        </Col>
                    </>
                )
            },
            {
                key: 'investimentos',
                label: 'Investimentos',
                disabled: !isManutencao && !planilha,
                children: (
                    <InvestimentosTab
                        solicitacaoId={solicitacao?.id}
                        orgaoId={solicitacao?.orgao?.id}
                        isManutencao={isManutencao}
                    />
                )
            },
            !isManutencao ? (
                {
                    key: 'demais-configuracoes-etapa',
                    label: 'Demais configurações da etapa',
                    disabled: !planilha,
                    children: (
                        <>
                            <InputObservacaoEtapa
                                solicitacaoId={solicitacao.id}
                                etapa={etapa}
                                onUpdateObservacao={onUpdateObservacao}
                                style={{
                                    padding: 0
                                }}
                            />
                        </>
                    )
                }
            ) : undefined
        ].filter((e) => e !== undefined) as any[];
    }, [
        solicitacao,
        onEtapaConcluida,
        onUpdateObservacao,
        onUpdateOrgao,
        etapa,
        planilha,
        isLoadingPlanilha,
        handleChangePlanilha,
        fileList,
        isDeletingPlanilha,
        isSubmitingPlanilha,
        downloadPlanilha,
        handleRemovePlanilha,
        handleClickUploadPlanilha,
        propsState,
        handleDeletePlanilha,
        handleChangeSelectedCategoria,
        selectedCategoriaId,
        categoriasOptions,
        filteredOrdenacaoFontes,
        fontesFromSelectedCategoria,
        categoriasDiferentesLength,
        fontesFavoritas,
        handleChangeFonteFavoritaEDescricao,
        handleChangeFonteCategoria,
        selectedCategoria,
        selectedRowKeys,
        handleClickAdicionarConta,
        handleClickEditarConta,
        handleClickEditarFontes,
        handleDeleteConta,
        isDeletingConta,
        handleEncerrarConta,
        isEncerrandoConta,
        handleClearAllFilters,
        hasActiveFilters,
        contas,
        handleResetRowSelection,
        rowSelection,
        columns,
        isManutencao,
        isLoadingOrdenacaoFontes,
        isLoadingFontesOrfas,
        countFontesOrfas,
        handleChangeSelectedCategoriaOrfas,
        selectedCategoriaOrfasId,
        categoriasOptionsOrfas,
        filteredOrdenacaoFontesOrfas,
        fontesFromSelectedCategoriaOrfas,
        selectedCategoriaOrfas,
        categoriasDiferentesLengthOrfas,
        handleChangeDescricaoFonteOrfa,
        handleChangeCategoriaFonteOrfa,
        handleDeleteFonte,
        onUpdateCategoriaFonte,
        onUpdateDescricaoFonte,
    ]);

    if (!solicitacao) {
        return null;
    }

    return (
        <>
            <Row>
                {
                    !isManutencao && (
                        <EtapaInfo
                            iniciadaEm={etapa?.iniciadaEm}
                            concluidaEm={etapa?.concluidaEm}
                            descricao={etapa?.etapa.descricao}
                            solicitacaoId={solicitacao.id}
                            etapa={etapa}
                            onEtapaIniciada={onEtapaIniciada}
                            onEtapaConcluida={onEtapaConcluida}
                        />
                    )
                }

                <div
                    style={{
                        width: '100%',
                        ...(!isManutencao ? {
                            opacity: etapa?.iniciadaEm ? 1 : 0.5,
                            pointerEvents: etapa?.iniciadaEm ? 'all' : 'none'
                        } : {})
                    }}
                >
                    <Tabs
                        type="line"
                        items={items}
                        activeKey={activeTabKey}
                        onChange={handleChangeTab}
                        style={{
                            margin: '0 10px',
                        }}
                    />
                </div>
            </Row>

            <ContaForm
                conta={selectedConta}
                onClose={handleCloseContaModal}
                solicitacao={solicitacao}
                visible={isContaModalVisible}
                onContaCreated={handleContaCreated}
                onContaUpdated={handleContaUpdated}
                onCreateFonte={onCreateFonte}
                onUpdateConta={onUpdateConta}
                onCreateConta={onCreateConta}
                onGetFontes={onGetFontes}
                onGetOrgaosVinculados={onGetOrgaosVinculados}
            />

            <EditarFontesModal
                visible={isEditarFontesModalVisible}
                onClose={handleCloseEditarFontesModal}
                solicitacao={solicitacao}
                selectedContas={selectedContas}
                onRemoveConta={handleRemoveSelectedConta}
                onGetFontes={onGetFontes}
                onCreateFonte={onCreateFonte}
                onUpdateContasFontes={onUpdateContasFontes}
                onSuccess={handleSuccessEditarFontes}
            />
        </>
    )
}
